<template>
    <div class="layout" :style="{ height: ssoExpanded ? 'auto' : '500px' }" v-if="RenderPage">
        <form class="login_form" @submit.prevent="OnLogin">
            <div class="page_head">
                <span class="page_title">Login</span>
                <span class="register_tag">
                    Don't have an account? <BLink :colorScheme="'blue'" :size="'sm'" :inline="true" :as="'router-link'"
                                           :to="steo0RouteWithQuery" :iconPosition="'left'" @click="() => { pushLoginEventToDataLayer('ob-register'); }">
                        Register
                    </BLink>
                </span>
            </div>

            <BInput class="login_input" v-model="emailAddress" id="login-email" data-test-id="input-login-email"
                    name="emailAddress" label="Email address*" :invalid="!!errors.emailAddress"
                    :error-message="errors.emailAddress" />

            <BInput :type="currentType" class="login_input" v-model="password" id="login-password"
                    data-test-id="input-login-password" name="password" label="Password*" :invalid="!!errors.password"
                    :error-message="errors.password">
                <template #icon="iconProps">
                    <ViewIcon v-if="currentType === type1" v-bind="{ ...iconProps }" @click="onToggle()" />
                    <ViewFillIcon v-else v-bind="{ ...iconProps }" @click="onToggle()" />
                </template>
            </BInput>
            <div class="login_footer">
                <BCheckbox :color-scheme="'black'" :label="'Remember me'" v-model="rememberMe" />
                <!-- Once go live of forgotten password is applied, swap the BLink definitions
                    <BLink :colorScheme="'blue'" :size="'sm'" :inline="true" :as="'router-link'"
                       :to="'/password/forgot'" @click="() => { pushLoginEventToDataLayer('ob-forgot-password'); }">-->
                <BLink :colorScheme="'blue'" :size="'md'" :inline="true" :to="`${address}`" :iconPosition="'right'" @click="() => { pushLoginEventToDataLayer('ob-forgot-password'); }">
                    Forgot Password
                </BLink>
            </div>
            <div class="login_button">
                <BButton colorScheme="blue" :loading="loading" id="button-login" data-test-id="button-login"
                         :disabled="disableButton || loading">
                    Log in with email
                </BButton>
            </div>

            <div class="register_tag">or</div>

            <div class="third_party_buttons">
                <BButton v-if="isThirdPartyProviderEnabled(ThirdPartyType.Orcid)" colorScheme="black"
                         variant="outline" :loading="orcidClicked" :disabled="thirdPartyLoading"
                         @click="thirdyPartyLogin(ThirdPartyType.Orcid)">
                    <img src="/static/images/orcid-svgrepo-com.svg" alt="OrcID Icon" class="orcid-icon" />
                    Continue with ORCID
                </BButton>

                <template v-if="ssoExpanded">
                    <BButton v-if="isThirdPartyProviderEnabled(ThirdPartyType.ESOT)" colorScheme="black"
                             variant="outline" :loading="esotClicked" :disabled="thirdPartyLoading"
                             @click="thirdyPartyLogin(ThirdPartyType.ESOT)">
                        <img src="/static/images/esot-grey.svg" alt="ESOT Icon" class="esot-icon" />
                        <span class="esot-text">Continue with ESOT</span>
                    </BButton>

                    <BButton v-if="isThirdPartyProviderEnabled(ThirdPartyType.LinkedIn)" colorScheme="black"
                             variant="outline" :loading="linkedInClicked" :disabled="thirdPartyLoading"
                             @click="thirdyPartyLogin(ThirdPartyType.LinkedIn)">
                        <LinkedinIcon :size="'md'" class="svg-icon" /> Continue with LinkedIn
                    </BButton>

                    <BButton v-if="isThirdPartyProviderEnabled(ThirdPartyType.Facebook)" colorScheme="black"
                             variant="outline" :loading="facebookClicked" :disabled="thirdPartyLoading"
                             @click="thirdyPartyLogin(ThirdPartyType.Facebook)">
                        <FacebookIcon :size="'md'" class="svg-icon"/> Continue with Facebook
                    </BButton>
                    <BButton v-if="isThirdPartyProviderEnabled(ThirdPartyType.Twitter)" colorScheme="black"
                             variant="outline" :loading="twitterClicked" :disabled="thirdPartyLoading"
                             @click="thirdyPartyLogin(ThirdPartyType.Twitter)">
                        <XTwitterIcon :size="'md'" class="svg-icon"/> Continue with X
                    </BButton>
                </template>
            </div>

            <span class="register_tag" v-if="!ssoExpanded">
                <BLink :colorScheme="'blue'" :size="'sm'" :inline="true" @click="expandSSOOptions">
                    See all SSO options
                </BLink>
            </span>

            <BSnackbarManager />
        </form>
    </div>

</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from "vue";
    import {
        BButton,
        BCheckbox,
        BInput,
        BLink,
        BSnackbarManager,
        clearAllSnackbarNotification,
        createSnackbarNotification
    } from "@frontiers/brink-ui";
    import { ViewFillIcon, ViewIcon, CloseCircleFillIcon, FacebookIcon, FundingIcon, LinkedinIcon, XTwitterIcon } from "@frontiers/brink-icons";
    import { useForm, useField } from "vee-validate";
    import { object, string } from "yup";
    import { useLoginStore } from "~/stores/loginStore";
    import type { Login } from "~/models/Login.ts";
    import { QueryStringHelper } from "~/helpers/QueryStringHelper";
    import { InputValidator } from "~/helpers/InputValidator";
    import { pushLoginEventToDataLayer } from "~/helpers/DataLayerHelper";
    import { RegistrationService } from "~/services/RegistrationService";
    import type { GetInvitationDataResponse } from "~/models/GetInvitationDataResponse";
    import { ThirdPartyType } from "~/enum/ThirdPartyType";
    import { useConfigurationStore } from "~/stores/configurationStore";
    import { useStep0Store } from "~/stores/step0Store";
    import { useThirdPartyStore } from "~/stores/thirdPartyStore";
    import type { ThirdPartyLogin } from "~/models/ThirdPartyLogin";
    import { ThirdPartyHelper } from "~/helpers/ThirdPartyHelper";
    import { ErrorCode } from "../enum/ErrorCode";
    import ErrorMessages from "~/helpers/ErrorMessages";
    import { useJourneyConfigurationStore } from "~/stores/journeyConfigurationStore";
    const journeyConfigurationStore = useJourneyConfigurationStore();

    const { errors } = useForm({
        validationSchema: object().shape({
            emailAddress: string()
                .required("Email address is required")
                .test("isValid", "Please enter a valid email", InputValidator.isValidEmail),
            password: string()
                .required("Password is required")
                .min(6, "Password must be at least 6 characters")
        })
    });

    const configurationStore = useConfigurationStore();
    const address = ref("/");

    const step0Store = useStep0Store();
    const configuration = configurationStore.config;

    const ssoExpanded = ref(false);

    const expandSSOOptions = () => {
        ssoExpanded.value = true;
    };

    const currentParams = QueryStringHelper.GetAllQueryStringParameters();

    const steo0RouteWithQuery = computed((): string => {
        if (currentParams == '') {
            return '/register';
        }
        else {
            return '/register' + '?' + currentParams;
        }
    });

    const thirdPartyStore = useThirdPartyStore();
    const thirdPartyLoading = computed((): boolean => thirdPartyStore.isLoading);

    const facebookClicked = ref<boolean>(false);
    const linkedInClicked = ref<boolean>(false);
    const orcidClicked = ref<boolean>(false);
    const esotClicked = ref<boolean>(false);
    const twitterCliked = ref<boolean>(false);
    const requestAuthToken: string | null = QueryStringHelper.GetRequestAuthToken()

    const thirdParty = ref<ThirdPartyLogin>();
    const RenderPage = ref(false);

    definePageMeta({
        alias: '/people/login',
        name: 'login'
    })

    const pageTitle = `${configuration.domainProperties.spaceName} | Login`;

    onMounted(async () => {

        const cookies = document.cookie.split(";");
        var sessionExists = !!cookies.find((cookie) => cookie.trim().startsWith("Cerberus"));

        if (!sessionExists) {
            RenderPage.value = true;
        }

        document.title = pageTitle;

        address.value = configuration.domainProperties.defaultWebsite + "/password/forgot";

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        if (urlParams.get("Email") && urlParams.get("Email") != "") {
            emailAddress.value = urlParams.get("Email") || "";
        }

        setValuesOnThirdPartyRedirect();

        const invitation: GetInvitationDataResponse = await RegistrationService.getInvitationData(requestAuthToken);

        if (invitation != null && invitation.invitationEmail.length > 0) {
            if (invitation.invitationEmail == invitation.actionEmail) {
                emailAddress.value = invitation.invitationEmail;
            }
        }
        else if (step0Store.emailAddress != "") {
            emailAddress.value = step0Store.emailAddress;
        }
    });

    const setValuesOnThirdPartyRedirect = () => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        let email = "";
        if (urlParams.get("Email") && urlParams.get("Email") != "") {
            email = urlParams.get("Email") || "";
            emailAddress.value = email;
        }        
        if (urlParams.get("ErrorCode")) {
            if (urlParams.get("ErrorCode")?.toString() == ErrorCode.AlreadyExistingAccount.toString()) {
                createSnackbarNotification({
                    message: ErrorMessages.getMessage(ErrorCode.AlreadyExistingAccount),
                    type: "error",
                    autoDismiss: true,
                    showCloseIcon: true
                })
            }
            else if (urlParams.get("ErrorCode")?.toString() == ErrorCode.AccountInactiveOrNotVerified.toString()) {
                createSnackbarNotification({
                    message: "This email belongs to an inactive account.",
                    type: "error",
                    autoDismiss: true,
                    showCloseIcon: true
                })
            }
            else if (urlParams.get("ErrorCode")?.toString() == ErrorCode.ThirdPartyAuthenticationFailed.toString()) {
                createSnackbarNotification({
                    message: "This account wasn't found. Please sign up to continue.",
                    type: "error",
                    autoDismiss: true,
                    showCloseIcon: true
                })
                newrelic.noticeError("User tried to login with a third party when there was no user registered for it. Email: " + email);
            }
            else if (urlParams.get("ErrorCode")?.toString() == ErrorCode.UserSuspended.toString()) {
                createSnackbarNotification({
                    message: ErrorMessages.getMessage(ErrorCode.UserSuspended),
                    type: "error",
                    autoDismiss: true,
                    showCloseIcon: true,
                    actionLabel: "Contact support team",
                    onActionClick: () => {
                        window.location.href = `mailto:${configuration.domainProperties.supportEmail}`;
                    }
                });
                newrelic.noticeError("User tried to login with a third party when it was already suspended. Email: " + email);
            }
            else {
                createSnackbarNotification({
                    message: "An unexpected error has happened. Please try again later.",
                    type: "error",
                    autoDismiss: true,
                    showCloseIcon: true
                })
                newrelic.noticeError("User failed to log in unexpectedly to Third Party. Email: " + email);
            }
        }
    }

    const { value: emailAddress } = useField<string>("emailAddress");
    const { value: password } = useField<string>("password");
    const rememberMe = ref(false);
    let currentType = ref("password");
    let type1: string = "text";
    let type2: string = "password";

    const loading = computed((): boolean => store.isLoading);
    const store = useLoginStore();

    const disableButton = computed(() => {
        return (
            !emailAddress.value ||
            !InputValidator.isValidEmail(emailAddress.value) ||
            !password.value ||
            password.value.length < 6
        );
    });


    const onToggle = () => {
        currentType.value = (currentType.value === type1) ? type2 : type1;
    }

    const OnLogin = async () => {

        if (Object.keys(errors.value).length > 0) {
            return;
        }

        pushLoginEventToDataLayer("ob-login");

        clearAllSnackbarNotification();

        const login = {
            email: emailAddress.value.trim(),
            password: password.value,
            rememberMe: rememberMe.value,
            requestAuthToken: requestAuthToken,
            returnUrl: QueryStringHelper.GetReturnUrl()
        };

        await store.postData(login);

    };

    function isThirdPartyProviderEnabled(thirdPartyEnum: ThirdPartyType): boolean {
        return ThirdPartyHelper.isThirdPartyProviderEnabled(thirdPartyEnum, configuration);
    }

    const thirdyPartyLogin = async (thirdPartyEnum: ThirdPartyType) => {
        pushToDataLayer(thirdPartyEnum);
        thirdParty.value = {
            thirdPartyProvider: thirdPartyEnum,
            requestAuthToken: requestAuthToken,
            returnUrl: "",
            actionReferral: null,
            isUserRegistered: false
        }
        await thirdPartyStore.postLoginData(thirdParty.value);
    }

    const pushToDataLayer = (thirdPartyEnum: ThirdPartyType) => {
        switch (thirdPartyEnum) {
            case ThirdPartyType.Facebook:
                pushLoginEventToDataLayer("ob-login-facebook");
                break;
            case ThirdPartyType.LinkedIn:
                pushLoginEventToDataLayer("ob-login-linkedin");
                break;
            case ThirdPartyType.ESOT:
                pushLoginEventToDataLayer("ob-login-esot");
                break;
            case ThirdPartyType.Orcid:
                pushLoginEventToDataLayer("ob-login-orcid");
                break;
        }
    }

    watch(thirdPartyLoading, (newLoaderValue: unknown) => {
        const loaderType = thirdParty.value?.thirdPartyProvider;
        const loaderValue = newLoaderValue as boolean;
        switch (loaderType) {
            case ThirdPartyType.Facebook:
                facebookClicked.value = loaderValue;
                break;
            case ThirdPartyType.LinkedIn:
                linkedInClicked.value = loaderValue;
                break;
            case ThirdPartyType.ESOT:
                esotClicked.value = loaderValue;
                break;
            case ThirdPartyType.Orcid:
                orcidClicked.value = loaderValue;
                break;
            case ThirdPartyType.Twitter:
                twitterCliked.value = loaderValue;
                break;
        }
    });

</script>

<style scoped lang="scss">
    .layout {
        max-width: 754px;
        height: 500px;
        border-radius: 5px;
        box-shadow: 0 0 2px lightgray;
        border: solid #f5f5f5 1px;
        padding: 1rem 2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: white;
        margin: 64px auto auto;
    }

    .login_form {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 24px;
        margin: 0 auto;
        width: 754px;

        @media (max-width: 800px) {
            width: auto;
            min-width: 350px;
        }
    }

    .register_tag {
        color: var(--Grey_Palette-Grey_55, #555);
        font-size: 16px;
        font-weight: 300;
        line-height: 20px;
    }

    .login_footer {
        display: flex;
        align-items: center;
        gap: 47px;
        width: 344px;
        justify-content: space-between;
        font-size: 12px;
    }

.login_form>div:nth-child(2),
    div:nth-child(3) {
        width: 344px;
    }

    .login_button {
        padding-top: 0.5rem;
    }

    .third_party_buttons {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 10px;
    }

    .orcid-icon {
        width: 20px;
        vertical-align: middle;
        position: relative;
        top: -1.5px;
    }

    .esot-icon {
        width: 38px;
        vertical-align: middle;
        position: relative;
        top: -2px;
    }

    .svg-icon {
        margin-top: -4px;
    }

    .esot-text {
        position: relative;
        left: -4px;
    }
.login_button {
    padding-top: 0.5rem;
}

    .b-form-control svg {
        cursor: pointer;
    }
</style>